import {
  useState,
  useEffect,
  useCallback,
} from "react";

import {
  Link,
} from "react-router-dom";

import {
  Calendar,
  TrendingUp,
  TrendingDown,
  Package,
  Eye,
  Edit2,
  FileDown,
  X,
  Search,
} from "lucide-react";

import { toast } from "sonner";

import {
  formatCurrency,
  formatDateRange,
} from "@/lib/formatters";

import {
  generateEventPDF,
  reconcileEvent,
} from "@/lib/pdfGenerator";

import ExportBar from "@/components/ExportBar";
import * as db from "@/lib/db";

const MONTHS = [
  { value: "all", label: "All Months" },
  { value: "1", label: "January" },
  { value: "2", label: "February" },
  { value: "3", label: "March" },
  { value: "4", label: "April" },
  { value: "5", label: "May" },
  { value: "6", label: "June" },
  { value: "7", label: "July" },
  { value: "8", label: "August" },
  { value: "9", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const SORT_OPTIONS = [
  { value: "latest", label: "Newest Date" },
  { value: "oldest", label: "Oldest Date" },
  { value: "highest_sales", label: "Highest Revenue" },
  { value: "lowest_sales", label: "Lowest Revenue" },
  { value: "highest_profit", label: "Highest Net Profit" },
  { value: "lowest_profit", label: "Lowest Net Profit" },
  { value: "highest_margin", label: "Highest Net Profit Margin" },
  { value: "lowest_margin", label: "Lowest Net Profit Margin" },
];

const PL_STATUS_OPTIONS = [
  { value: "all", label: "All P&L Status" },
  { value: "profitable", label: "Profitable" },
  { value: "loss", label: "Loss-making" },
  { value: "breakeven", label: "Break-even" },
];

const EVENT_STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "completed", label: "Completed" },
  { value: "draft", label: "Draft" },
];

const DEFAULT_FILTERS = {
  month: "all",
  year: "all",
  sortBy: "latest",
  activityTypeId: "all",
  eventStatus: "all",
  plStatus: "all",
  search: "",
};

/*
 * ============================================================
 * UNIT HELPERS
 * ============================================================
 */

const getRecordedUnits = (
  event
) =>
  Number(
    event?.totalProductQuantitySold ||
      0
  );

const getEstimatedUnits = (
  event
) =>
  Number(
    event?.estimatedUnrecordedWholeCups ||
      0
  );

const getTotalUnitsSold = (
  event
) =>
  getRecordedUnits(event) +
  getEstimatedUnits(event);

/*
 * ============================================================
 * RECONCILIATION HELPERS
 * ============================================================
 */

const getReconciliationLabel = (
  status
) => {
  if (
    status ===
    "Fully Reconciled"
  ) {
    return "Fully reconciled";
  }

  if (
    status ===
    "Unrecorded Cup Sales Estimated"
  ) {
    return "Estimated from unrecorded sales";
  }

  if (
    status ===
    "Recorded Product Sales Exceed Total Sales Collected"
  ) {
    return "Recorded product sales exceed collected sales";
  }

  return "Manual review required";
};

const getReconciliationColor = (
  status
) => {
  if (
    status ===
    "Fully Reconciled"
  ) {
    return "text-emerald-700";
  }

  if (
    status ===
    "Unrecorded Cup Sales Estimated"
  ) {
    return "text-amber-700";
  }

  if (
    status ===
    "Recorded Product Sales Exceed Total Sales Collected"
  ) {
    return "text-red-700";
  }

  return "text-slate-500";
};

const EventHistory = () => {
  const [events, setEvents] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  /*
   * ============================================================
   * QUICK VIEW
   * ============================================================
   */

  const [viewTarget, setViewTarget] = useState(null);

  /*
   * ============================================================
   * FILTERS
   * ============================================================
   */

  const [month, setMonth] = useState(DEFAULT_FILTERS.month);
  const [year, setYear] = useState(DEFAULT_FILTERS.year);
  const [sortBy, setSortBy] = useState(DEFAULT_FILTERS.sortBy);

  const [
    activityTypeId,
    setActivityTypeId,
  ] = useState(
    DEFAULT_FILTERS.activityTypeId
  );

  const [
    eventStatus,
    setEventStatus,
  ] = useState(
    DEFAULT_FILTERS.eventStatus
  );

  const [
    plStatus,
    setPlStatus,
  ] = useState(
    DEFAULT_FILTERS.plStatus
  );

  const [search, setSearch] = useState(DEFAULT_FILTERS.search);

  const [
    searchDebounced,
    setSearchDebounced,
  ] = useState(
    DEFAULT_FILTERS.search
  );

  /*
   * ============================================================
   * FETCH EVENTS
   * ============================================================
   */

  const fetchEvents =
    useCallback(
      async () => {
        try {
          setLoading(true);

          const data =
            await db.listEventsSummary({
              month,
              year,
              sortBy,
              activityTypeId,
              eventStatus,
              plStatus,
              search: searchDebounced,
            });

          setEvents(data);
        } catch (error) {
          console.error(
            "Error fetching events:",
            error
          );

          toast.error(
            "Failed to load events"
          );
        } finally {
          setLoading(false);
        }
      },
      [
        month,
        year,
        sortBy,
        activityTypeId,
        eventStatus,
        plStatus,
        searchDebounced,
      ]
    );

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  /*
   * ============================================================
   * SEARCH DEBOUNCE
   * ============================================================
   */

  useEffect(() => {
    const t =
      setTimeout(
        () =>
          setSearchDebounced(
            search
          ),
        250
      );

    return () =>
      clearTimeout(t);
  }, [search]);

  /*
   * ============================================================
   * REFERENCE DATA
   * ============================================================
   */

  useEffect(() => {
    (async () => {
      try {
        const [
          years,
          ats,
        ] =
          await Promise.all([
            db.listEventYears(),

            db.listActivityTypes({
              includeArchived: true,
            }),
          ]);

        setAvailableYears(years);
        setActivityTypes(ats);
      } catch (e) {
        console.error(
          "Error loading filter data:",
          e
        );
      }
    })();
  }, []);

  /*
   * ============================================================
   * PDF DOWNLOAD
   * ============================================================
   */

  const handleDownloadPDF =
    async (
      e,
      event
    ) => {
      e.preventDefault();
      e.stopPropagation();

      if (downloadingId) {
        return;
      }

      setDownloadingId(event.id);

      const toastId =
        toast.loading(
          "Generating Management P&L PDF..."
        );

      try {
        const full =
          await db.getEvent(
            event.id
          );

        if (
          !reconcileEvent(full)
        ) {
          toast.error(
            "Unable to generate P&L PDF because the event totals do not reconcile. Please review the event details.",
            {
              id: toastId,
            }
          );

          return;
        }

        await new Promise(
          (resolve) =>
            setTimeout(
              resolve,
              50
            )
        );

        generateEventPDF(full);

        toast.success(
          "Management P&L PDF downloaded successfully.",
          {
            id: toastId,
          }
        );
      } catch (error) {
        console.error(
          "PDF generation failed:",
          error
        );

        toast.error(
          "Unable to generate the Management P&L PDF. Please try again.",
          {
            id: toastId,
          }
        );
      } finally {
        setDownloadingId(null);
      }
    };

  /*
   * ============================================================
   * PROFIT STYLES
   * ============================================================
   */

  const getProfitStyle = (
    profit
  ) => {
    if (profit > 0) {
      return "text-emerald-700";
    }

    if (profit < 0) {
      return "text-red-600";
    }

    return "text-slate-500";
  };

  const getProfitBg = (
    profit
  ) => {
    if (profit > 0) {
      return "bg-emerald-50 border-emerald-200";
    }

    if (profit < 0) {
      return "bg-red-50 border-red-200";
    }

    return "bg-slate-100 border-slate-200";
  };

  /*
   * ============================================================
   * FILTER ACTIONS
   * ============================================================
   */

  const clearAll = () => {
    setMonth(DEFAULT_FILTERS.month);
    setYear(DEFAULT_FILTERS.year);
    setSortBy(DEFAULT_FILTERS.sortBy);

    setActivityTypeId(
      DEFAULT_FILTERS.activityTypeId
    );

    setEventStatus(
      DEFAULT_FILTERS.eventStatus
    );

    setPlStatus(
      DEFAULT_FILTERS.plStatus
    );

    setSearch(
      DEFAULT_FILTERS.search
    );
  };

  const clearSearchOnly =
    () =>
      setSearch("");

  const filtersActive =
    month !== DEFAULT_FILTERS.month ||
    year !== DEFAULT_FILTERS.year ||
    sortBy !== DEFAULT_FILTERS.sortBy ||
    activityTypeId !== DEFAULT_FILTERS.activityTypeId ||
    eventStatus !== DEFAULT_FILTERS.eventStatus ||
    plStatus !== DEFAULT_FILTERS.plStatus ||
    search.trim() !== "";

  const selectClass =
    "w-full bg-white border border-slate-200 focus:border-[#fa9d1c] focus:ring-1 focus:ring-[#fa9d1c] rounded-xl h-11 px-3 text-sm text-slate-800 appearance-none cursor-pointer transition-colors";

  const activityTypeName = (
    id
  ) => {
    const found =
      activityTypes.find(
        (a) =>
          a.id === id
      );

    return found
      ? found.name
      : "—";
  };

  /*
   * ============================================================
   * QUICK VIEW CALCULATIONS
   * ============================================================
   */

  const viewProfit =
    Number(
      viewTarget?.netProfit ||
        0
    );

  const viewRevenue =
    Number(
      viewTarget?.totalSalesCollected !=
        null
        ? viewTarget.totalSalesCollected
        : viewTarget?.totalSales ||
            0
    );

  /*
   * COGS
   */

  const viewRecordedProductCost =
    Number(
      viewTarget?.totalProductCost ||
        0
    );

  const viewEstimatedUnrecordedCogs =
    Number(
      viewTarget?.estimatedUnrecordedCogs ||
        0
    );

  const viewTotalCogs =
    Number(
      viewTarget?.totalCostOfGoodsSold ??
        (
          viewRecordedProductCost +
          viewEstimatedUnrecordedCogs
        )
    );

  /*
   * EVENT EXPENSES
   */

  const viewRental =
    Number(
      viewTarget?.rentalCost ||
        0
    );

  const viewStaffWages =
    Number(
      viewTarget?.totalStaffWages ||
        0
    );

  const viewTransport =
    Number(
      viewTarget?.transportCost ||
        0
    );

  const viewOtherEventCost =
    Number(
      viewTarget?.otherCost ||
        0
    );

  const viewEventExpenses =
    viewRental +
    viewStaffWages +
    viewTransport +
    viewOtherEventCost;

  /*
   * UNITS
   */

  const viewRecordedUnits =
    getRecordedUnits(
      viewTarget
    );

  const viewEstimatedUnits =
    getEstimatedUnits(
      viewTarget
    );

  const viewTotalUnits =
    getTotalUnitsSold(
      viewTarget
    );

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-16">

      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6 space-y-6">

        {/* =====================================================
            HEADER
        ====================================================== */}

        <div className="flex items-start justify-between gap-3 flex-wrap">

          <div>
            <h1
              className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#193f7d]"
              style={{
                fontFamily:
                  "Outfit, sans-serif",
              }}
              data-testid="event-history-title"
            >
              Event History
            </h1>

            <p className="text-sm text-slate-600 mt-1">
              All saved bazaar &amp; event P&amp;L records
            </p>
          </div>

          <ExportBar
            testIdPrefix="history-export"
            buildReport={() => {
              const cols = [
                {
                  key: "eventName",
                  label: "Event / Activity",
                },
                {
                  key: "activityType",
                  label: "Activity Type",
                },
                {
                  key: "startDate",
                  label: "Start",
                },
                {
                  key: "endDate",
                  label: "End",
                },
                {
                  key: "totalSalesCollected",
                  label: "Revenue",
                  type: "currency",
                },
                {
                  key: "totalProductCost",
                  label: "Product Cost",
                  type: "currency",
                },
                {
                  key: "netProfit",
                  label: "Net Profit",
                  type: "currency",
                },
                {
                  key: "profitMargin",
                  label: "Margin %",
                },
                {
                  key: "status",
                  label: "Status",
                },
              ];

              const rows =
                events.map(
                  (e) => ({
                    ...e,

                    activityType:
                      activityTypeName(
                        e.activityTypeId
                      ),

                    profitMargin:
                      `${(
                        e.profitMargin ||
                        0
                      ).toFixed(
                        2
                      )}%`,
                  })
                );

              const totals = {
                eventName: "TOTAL",

                totalSalesCollected:
                  rows.reduce(
                    (
                      s,
                      r
                    ) =>
                      s +
                      Number(
                        r.totalSalesCollected ||
                          0
                      ),
                    0
                  ),

                totalProductCost:
                  rows.reduce(
                    (
                      s,
                      r
                    ) =>
                      s +
                      Number(
                        r.totalProductCost ||
                          0
                      ),
                    0
                  ),

                netProfit:
                  rows.reduce(
                    (
                      s,
                      r
                    ) =>
                      s +
                      Number(
                        r.netProfit ||
                          0
                      ),
                    0
                  ),
              };

              return {
                title:
                  "Event History Summary",

                subtitle:
                  "Filtered event history",

                filename:
                  `Gao-Chill-Event-History-${new Date()
                    .toISOString()
                    .slice(
                      0,
                      10
                    )}`,

                columns: cols,
                rows,
                totals,

                sections: [
                  {
                    heading:
                      `Events (${events.length})`,

                    columns: cols,
                    rows,
                    totals,
                  },
                ],

                metrics: [
                  {
                    label: "Events",
                    value:
                      events.length,
                  },
                  {
                    label:
                      "Total Revenue",

                    value:
                      `RM ${totals.totalSalesCollected.toFixed(
                        2
                      )}`,
                  },
                  {
                    label:
                      "Total Net Profit",

                    value:
                      `RM ${totals.netProfit.toFixed(
                        2
                      )}`,
                  },
                ],
              };
            }}
          />

        </div>

        {/* =====================================================
            FILTER PANEL
        ====================================================== */}

        <section
          className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-sm space-y-4"
          data-testid="event-history-filters"
        >

          <div className="relative">

            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />

            <input
              type="text"
              value={search}
              onChange={(e) =>
                setSearch(
                  e.target.value
                )
              }
              placeholder="Search event name, notes, month, year…"
              className="w-full bg-white border border-slate-200 focus:border-[#fa9d1c] focus:ring-1 focus:ring-[#fa9d1c] rounded-xl h-11 pl-9 pr-9 text-sm text-slate-800 transition-colors"
              data-testid="filter-search-input"
            />

            {search && (
              <button
                type="button"
                onClick={
                  clearSearchOnly
                }
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1 rounded-full"
                data-testid="filter-clear-search-btn"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}

          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">

            <FilterSelect
              label="Activity Type"
              value={activityTypeId}
              onChange={setActivityTypeId}
              className={selectClass}
              testId="filter-activity-type-select"
            >
              <option value="all">
                All Activity Types
              </option>

              {activityTypes.map(
                (at) => (
                  <option
                    key={at.id}
                    value={at.id}
                  >
                    {at.name}
                    {at.isArchived
                      ? " (archived)"
                      : ""}
                  </option>
                )
              )}
            </FilterSelect>

            <FilterSelect
              label="Month"
              value={month}
              onChange={setMonth}
              className={selectClass}
              testId="filter-month-select"
            >
              {MONTHS.map(
                (m) => (
                  <option
                    key={m.value}
                    value={m.value}
                  >
                    {m.label}
                  </option>
                )
              )}
            </FilterSelect>

            <FilterSelect
              label="Year"
              value={year}
              onChange={setYear}
              className={selectClass}
              testId="filter-year-select"
            >
              <option value="all">
                All Years
              </option>

              {availableYears.map(
                (y) => (
                  <option
                    key={y}
                    value={y}
                  >
                    {y}
                  </option>
                )
              )}
            </FilterSelect>

            <FilterSelect
              label="P&L Status"
              value={plStatus}
              onChange={setPlStatus}
              className={selectClass}
              testId="filter-pl-status-select"
            >
              {PL_STATUS_OPTIONS.map(
                (o) => (
                  <option
                    key={o.value}
                    value={o.value}
                  >
                    {o.label}
                  </option>
                )
              )}
            </FilterSelect>

            <FilterSelect
              label="Status"
              value={eventStatus}
              onChange={setEventStatus}
              className={selectClass}
              testId="filter-event-status-select"
            >
              {EVENT_STATUS_OPTIONS.map(
                (o) => (
                  <option
                    key={o.value}
                    value={o.value}
                  >
                    {o.label}
                  </option>
                )
              )}
            </FilterSelect>

            <FilterSelect
              label="Sort By"
              value={sortBy}
              onChange={setSortBy}
              className={selectClass}
              testId="filter-sort-select"
            >
              {SORT_OPTIONS.map(
                (s) => (
                  <option
                    key={s.value}
                    value={s.value}
                  >
                    {s.label}
                  </option>
                )
              )}
            </FilterSelect>

          </div>

          {filtersActive && (
            <div className="flex justify-end">

              <button
                onClick={clearAll}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:border-[#193f7d] hover:bg-slate-50 text-xs font-semibold text-slate-600 hover:text-[#193f7d] transition-colors"
                data-testid="filter-clear-btn"
              >
                <X className="w-3.5 h-3.5" />
                Reset Filters
              </button>

            </div>
          )}

        </section>

        {/* =====================================================
            EVENT LIST
        ====================================================== */}

        {loading ? (
          <div
            className="text-center py-12 text-slate-500"
            data-testid="loading-events"
          >
            Loading events...
          </div>
        ) : events.length === 0 ? (
          <div
            className="text-center py-16 bg-white border border-slate-200 rounded-2xl"
            data-testid="no-events"
          >
            <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />

            {filtersActive ? (
              <>
                <p className="text-slate-600 font-medium">
                  No events found for the selected filters.
                </p>

                <p className="text-sm text-slate-400 mt-2">
                  Try a different filter combination or reset all filters
                </p>
              </>
            ) : (
              <>
                <p className="text-slate-600 font-medium">
                  No events saved yet
                </p>

                <p className="text-sm text-slate-400 mt-2">
                  Head to "Add Event" to record your first activity
                </p>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4">

            {events.map(
              (event) => {
                const profit =
                  event.netProfit ||
                  0;

                const ProfitIcon =
                  profit > 0
                    ? TrendingUp
                    : profit < 0
                    ? TrendingDown
                    : Package;

                const isDraft =
                  event.status ===
                  "draft";

                const recordedUnits =
                  getRecordedUnits(
                    event
                  );

                const estimatedUnits =
                  getEstimatedUnits(
                    event
                  );

                const totalUnits =
                  getTotalUnitsSold(
                    event
                  );

                return (
                  <div
                    key={event.id}
                    className="bg-white border border-slate-200 hover:border-[#193f7d]/40 rounded-2xl p-5 sm:p-6 transition-colors shadow-sm"
                    data-testid={`event-card-${event.id}`}
                  >

                    {/* EVENT HEADER */}

                    <div className="flex items-start justify-between gap-4 mb-4">

                      <div className="flex-1 min-w-0">

                        <div className="flex items-center gap-2 flex-wrap">

                          <h3
                            className="text-lg font-medium text-slate-900 truncate"
                            data-testid={`event-name-${event.id}`}
                          >
                            {event.eventName}
                          </h3>

                          {isDraft && (
                            <span
                              className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5"
                              data-testid={`event-status-badge-${event.id}`}
                            >
                              Draft
                            </span>
                          )}

                        </div>

                        <p
                          className="text-xs font-semibold text-[#193f7d] mt-1"
                          data-testid={`event-activity-type-${event.id}`}
                        >
                          {activityTypeName(
                            event.activityTypeId
                          )}
                        </p>

                        <p
                          className="text-sm text-slate-600 mt-1"
                          data-testid={`event-dates-${event.id}`}
                        >
                          {formatDateRange(
                            event.startDate,
                            event.endDate
                          )}
                        </p>

                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mt-1">
                          {event.totalEventDays}{" "}
                          {event.totalEventDays === 1
                            ? "Day"
                            : "Days"}{" "}
                          ·{" "}
                          {totalUnits}{" "}
                          Units Sold
                        </p>

                        {estimatedUnits > 0 && (
                          <p className="text-[10px] text-slate-400 mt-1">
                            {recordedUnits} recorded ·{" "}
                            {estimatedUnits} estimated
                          </p>
                        )}

                      </div>

                      <div
                        className={`flex-shrink-0 flex items-center gap-2 px-3 py-1.5 rounded-full border ${getProfitBg(
                          profit
                        )}`}
                      >
                        <ProfitIcon
                          className={`w-4 h-4 ${getProfitStyle(
                            profit
                          )}`}
                        />

                        <span
                          className={`text-xs font-bold ${getProfitStyle(
                            profit
                          )}`}
                        >
                          {(event.profitMargin || 0).toFixed(
                            2
                          )}
                          %
                        </span>
                      </div>

                    </div>

                    {/* METRICS */}

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">

                      <MetricBox
                        label="Revenue"
                        value={formatCurrency(
                          event.totalSalesCollected != null
                            ? event.totalSalesCollected
                            : event.totalSales
                        )}
                        testId={`event-collected-${event.id}`}
                      />

                      <MetricBox
                        label="Product Cost"
                        value={formatCurrency(
                          event.totalProductCost ||
                            0
                        )}
                      />

                      <MetricBox
                        label="Total Expenses"
                        value={formatCurrency(
                          (event.totalCost || 0) -
                            (event.totalProductCost || 0)
                        )}
                      />

                      <MetricBox
                        label="Net Profit / Loss"
                        value={formatCurrency(
                          profit
                        )}
                        valueClass={getProfitStyle(
                          profit
                        )}
                        testId={`event-net-profit-${event.id}`}
                      />

                      <MetricBox
                        label="Net Margin"
                        value={`${(
                          event.profitMargin ||
                          0
                        ).toFixed(
                          2
                        )}%`}
                      />

                    </div>

                    {/* RECONCILIATION */}

                    <div className="mb-4">

                      <p
                        className={`text-[11px] ${getReconciliationColor(
                          event.reconciliationStatus
                        )}`}
                        data-testid={`event-recon-status-${event.id}`}
                      >
                        <span className="font-semibold">
                          Reconciliation:
                        </span>{" "}
                        {getReconciliationLabel(
                          event.reconciliationStatus
                        )}
                      </p>

                    </div>

                    {/* ACTIONS */}

                    <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-100 flex-wrap">

                      <button
                        type="button"
                        onClick={() =>
                          setViewTarget(
                            event
                          )
                        }
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:border-[#193f7d] hover:bg-blue-50 text-xs font-semibold text-slate-600 hover:text-[#193f7d] transition-colors"
                        data-testid={`view-event-btn-${event.id}`}
                      >
                        <Eye className="w-3.5 h-3.5" />
                        View
                      </button>

                      <Link
                        to={`/events/${event.id}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:border-[#193f7d] hover:bg-slate-50 text-xs font-semibold text-slate-600 hover:text-[#193f7d] transition-colors"
                        data-testid={`edit-event-btn-${event.id}`}
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                        Edit
                      </Link>

                      <button
                        type="button"
                        onClick={(e) =>
                          handleDownloadPDF(
                            e,
                            event
                          )
                        }
                        disabled={
                          downloadingId ===
                          event.id
                        }
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 bg-white hover:border-[#193f7d] hover:bg-slate-50 text-xs font-semibold text-[#193f7d] transition-colors disabled:opacity-60"
                        data-testid={`download-pdf-btn-${event.id}`}
                      >
                        <FileDown className="w-3.5 h-3.5" />

                        {downloadingId ===
                        event.id
                          ? "Generating..."
                          : "Download PDF"}
                      </button>

                    </div>

                  </div>
                );
              }
            )}

          </div>
        )}

      </div>

      {/* =======================================================
          QUICK VIEW MODAL
      ======================================================== */}

      {viewTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onMouseDown={() =>
            setViewTarget(
              null
            )
          }
          data-testid="event-view-modal"
        >

          <div
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onMouseDown={(e) =>
              e.stopPropagation()
            }
          >

            {/* HEADER */}

            <div className="sticky top-0 z-10 bg-white border-b border-slate-200 rounded-t-2xl px-5 py-4 flex items-start justify-between gap-4">

              <div className="min-w-0">

                <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">
                  Event P&amp;L Summary
                </p>

                <h2
                  className="text-xl font-semibold text-[#193f7d] mt-1 truncate"
                  style={{
                    fontFamily:
                      "Outfit, sans-serif",
                  }}
                >
                  {viewTarget.eventName}
                </h2>

                <p className="text-xs text-slate-500 mt-1">
                  {activityTypeName(
                    viewTarget.activityTypeId
                  )}
                  {" · "}
                  {formatDateRange(
                    viewTarget.startDate,
                    viewTarget.endDate
                  )}
                </p>

              </div>

              <button
                type="button"
                onClick={() =>
                  setViewTarget(
                    null
                  )
                }
                className="p-2 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>

            </div>

            <div className="p-5 space-y-5">

              {/* MAIN RESULT */}

              <div
                className={`rounded-2xl border p-4 ${
                  viewProfit >= 0
                    ? "bg-emerald-50 border-emerald-200"
                    : "bg-red-50 border-red-200"
                }`}
              >

                <div className="flex items-start justify-between gap-4">

                  <div>

                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      Net Profit / Loss
                    </p>

                    <p
                      className={`text-2xl font-mono font-bold mt-1 ${getProfitStyle(
                        viewProfit
                      )}`}
                    >
                      {formatCurrency(
                        viewProfit
                      )}
                    </p>

                  </div>

                  <div className="text-right">

                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                      Net Margin
                    </p>

                    <p
                      className={`text-xl font-mono font-bold mt-1 ${getProfitStyle(
                        viewProfit
                      )}`}
                    >
                      {(viewTarget.profitMargin || 0).toFixed(
                        2
                      )}
                      %
                    </p>

                  </div>

                </div>

              </div>

              {/* P&L SUMMARY */}

              <div>

                <h3 className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-3">
                  P&amp;L Summary
                </h3>

                <div className="border border-slate-200 rounded-xl overflow-hidden">

                  {/* REVENUE */}

                  <SummaryRow
                    label="Revenue"
                    value={formatCurrency(
                      viewRevenue
                    )}
                  />

                  {/* COGS */}

                  <div className="border-t border-slate-100 bg-slate-50/60 px-3 py-3">

                    <div className="flex items-center justify-between gap-4">

                      <p className="text-sm font-bold text-slate-800">
                        Less: Cost of Goods Sold
                      </p>

                      <p className="font-mono text-sm font-bold text-slate-900">
                        {formatCurrency(
                          -viewTotalCogs
                        )}
                      </p>

                    </div>

                    <div className="mt-2 space-y-1.5 pl-2">

                      <BreakdownRow
                        label="Recorded Product Cost"
                        value={
                          viewRecordedProductCost
                        }
                      />

                      <BreakdownRow
                        label="Estimated Unrecorded COGS"
                        value={
                          viewEstimatedUnrecordedCogs
                        }
                      />

                      <div className="border-t border-slate-200 pt-1.5 mt-1.5">

                        <BreakdownRow
                          label="Total COGS"
                          value={
                            viewTotalCogs
                          }
                          strong
                        />

                      </div>

                    </div>

                  </div>

                  {/* EVENT EXPENSES */}

                  <div className="border-t border-slate-100 px-3 py-3">

                    <div className="flex items-center justify-between gap-4">

                      <p className="text-sm font-bold text-slate-800">
                        Less: Event Expenses
                      </p>

                      <p className="font-mono text-sm font-bold text-slate-900">
                        {formatCurrency(
                          -viewEventExpenses
                        )}
                      </p>

                    </div>

                    <div className="mt-2 space-y-1.5 pl-2">

                      <BreakdownRow
                        label="Rental"
                        value={
                          viewRental
                        }
                      />

                      <BreakdownRow
                        label="Staff Wages"
                        value={
                          viewStaffWages
                        }
                      />

                      <BreakdownRow
                        label="Transport"
                        value={
                          viewTransport
                        }
                      />

                      <BreakdownRow
                        label="Other Event Cost"
                        value={
                          viewOtherEventCost
                        }
                      />

                      <div className="border-t border-slate-200 pt-1.5 mt-1.5">

                        <BreakdownRow
                          label="Total Event Expenses"
                          value={
                            viewEventExpenses
                          }
                          strong
                        />

                      </div>

                    </div>

                  </div>

                  {/* FINAL RESULT */}

                  <div className="border-t border-slate-200 bg-slate-50">

                    <SummaryRow
                      label="Net Profit / Loss"
                      value={formatCurrency(
                        viewProfit
                      )}
                      strong
                      valueClass={getProfitStyle(
                        viewProfit
                      )}
                    />

                    <div className="px-3 pb-3 flex items-center justify-between gap-4">

                      <p className="text-sm font-bold text-slate-900">
                        Net Margin
                      </p>

                      <p
                        className={`font-mono text-base font-bold ${getProfitStyle(
                          viewProfit
                        )}`}
                      >
                        {(viewTarget.profitMargin || 0).toFixed(
                          2
                        )}
                        %
                      </p>

                    </div>

                  </div>

                </div>

              </div>

              {/* EVENT INFO */}

              <div>

                <h3 className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-3">
                  Event Information
                </h3>

                <div className="grid grid-cols-2 gap-4">

                  <QuickField
                    label="Activity Type"
                    value={activityTypeName(
                      viewTarget.activityTypeId
                    )}
                  />

                  <QuickField
                    label="Event Status"
                    value={
                      viewTarget.status ||
                      "—"
                    }
                  />

                  <QuickField
                    label="Event Days"
                    value={`${viewTarget.totalEventDays || 0}`}
                  />

                  <div>

                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      Total Units Sold
                    </p>

                    <p className="text-lg font-mono font-bold text-slate-900 mt-1">
                      {viewTotalUnits}
                    </p>

                    <p className="text-[10px] text-slate-500 mt-1">
                      Recorded:{" "}
                      <span className="font-semibold">
                        {viewRecordedUnits}
                      </span>

                      {" · "}

                      Estimated Unrecorded:{" "}
                      <span className="font-semibold">
                        {viewEstimatedUnits}
                      </span>
                    </p>

                  </div>

                </div>

                {/* SMALL RECONCILIATION LINE */}

                <div className="border-t border-slate-100 mt-4 pt-3">

                  <p
                    className={`text-[11px] ${getReconciliationColor(
                      viewTarget.reconciliationStatus
                    )}`}
                  >
                    <span className="font-semibold">
                      Reconciliation:
                    </span>{" "}

                    {getReconciliationLabel(
                      viewTarget.reconciliationStatus
                    )}
                  </p>

                </div>

              </div>

              {/* CLOSE */}

              <div className="border-t border-slate-200 pt-4 flex justify-end">

                <button
                  type="button"
                  onClick={() =>
                    setViewTarget(
                      null
                    )
                  }
                  className="px-5 py-2 rounded-full border border-slate-200 bg-white hover:border-[#193f7d] hover:bg-slate-50 text-sm font-semibold text-slate-600 hover:text-[#193f7d] transition-colors"
                >
                  Close
                </button>

              </div>

            </div>

          </div>

        </div>
      )}

    </div>
  );
};

/*
 * ============================================================
 * FILTER SELECT
 * ============================================================
 */

const FilterSelect = ({
  label,
  value,
  onChange,
  className,
  testId,
  children,
}) => (
  <div>

    <label className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500 mb-1.5 block">
      {label}
    </label>

    <select
      value={value}
      onChange={(e) =>
        onChange(
          e.target.value
        )
      }
      className={className}
      data-testid={testId}
    >
      {children}
    </select>

  </div>
);

/*
 * ============================================================
 * EVENT CARD METRIC
 * ============================================================
 */

const MetricBox = ({
  label,
  value,
  valueClass = "text-slate-900",
  testId,
}) => (
  <div>

    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
      {label}
    </p>

    <p
      className={`font-mono font-semibold mt-1 text-sm ${valueClass}`}
      data-testid={testId}
    >
      {value}
    </p>

  </div>
);

/*
 * ============================================================
 * QUICK VIEW SUMMARY ROW
 * ============================================================
 */

const SummaryRow = ({
  label,
  value,
  strong = false,
  valueClass = "text-slate-900",
}) => (
  <div className="flex items-center justify-between gap-4 px-3 py-3">

    <p
      className={`text-sm ${
        strong
          ? "font-bold text-slate-900"
          : "font-medium text-slate-600"
      }`}
    >
      {label}
    </p>

    <p
      className={`font-mono ${
        strong
          ? "font-bold text-base"
          : "font-semibold text-sm"
      } ${valueClass}`}
    >
      {value}
    </p>

  </div>
);

/*
 * ============================================================
 * BREAKDOWN ROW
 * ============================================================
 */

const BreakdownRow = ({
  label,
  value,
  strong = false,
}) => (
  <div className="flex items-center justify-between gap-4">

    <p
      className={`text-xs ${
        strong
          ? "font-bold text-slate-700"
          : "font-medium text-slate-500"
      }`}
    >
      {label}
    </p>

    <p
      className={`font-mono text-xs ${
        strong
          ? "font-bold text-slate-900"
          : "font-semibold text-slate-700"
      }`}
    >
      {formatCurrency(
        value
      )}
    </p>

  </div>
);

/*
 * ============================================================
 * QUICK VIEW FIELD
 * ============================================================
 */

const QuickField = ({
  label,
  value,
}) => (
  <div>

    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
      {label}
    </p>

    <p className="text-sm font-semibold text-slate-800 mt-1">
      {value}
    </p>

  </div>
);

export default EventHistory;
